<script setup lang="ts">
/**
 * UI-kit component
 * Category: Inputs
 * Name: 'Inputs'
 * FIGMA: 'https://www.figma.com/design/CFyOwQUjj7uVRQTc03muvG/Demo-CS?node-id=7681-4708&m=dev'
 */
import examples from 'libphonenumber-js/mobile/examples'
import { vMaska } from 'maska/vue'
import { reactive, ref } from 'vue'
import { getExampleNumber, parsePhoneNumber } from 'libphonenumber-js'
import { VueTelInput } from 'vue-tel-input'

import type { ChangeEvent } from '@brighterly/lib-core-types/ui/inputs/interfaces'
import type { CountryCode } from 'libphonenumber-js'
import type { CountryPhoneData, ValidatePhoneData } from '@brighterly/lib-core-types/ui/inputs/interfaces'
import type { InputMode } from '@brighterly/lib-core-types/ui/inputs/types'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import 'vue-tel-input/vue-tel-input.css'
import { ignoredCountries } from '@shared/config'
import { hasProp } from '@shared/lib/object'
import { TextTertiary } from '@shared/ui/typography'

defineOptions({
  name: 'PhoneInput',
})

const props = withDefaults(defineProps<PhoneInputProps>(), {
  name: '',
  label: '',
  value: '',
  disabled: false,
  invalidMsg: '',
  changeTrigger: 0,
  dataQa: null,
  required: false,
  info: '',
  mode: 'normal',
})

const emit = defineEmits<PhoneInputEmit>()

dayjs.extend(utc)
dayjs.extend(timezone)

type PhoneInputEmit = {
  change: [ChangeEvent]
  input: []
}

const inputRef = ref(null)

interface PhoneInputProps {
  id: string
  name?: string
  label?: string
  value?: string
  disabled?: boolean
  invalidMsg?: string
  changeTrigger?: number
  countryByTz: Record<string, string>
  dataQa?: string | null
  required?: boolean
  info?: string
  mode?: InputMode
}

interface PhoneInputState {
  isChanged: boolean
  validate: boolean
  validateSuccess: boolean
  validateWrong: boolean
  phone: string
  fullPhone: string
  mask: string
  countryCode: CountryCode
  options: {
    id: string
    maxlength: number | undefined
    placeholder: string
    required: true
    type: string
    styleClasses: string
  }
  tz: string
  isInitEnd: boolean
  isSuccess: boolean
}

const state: PhoneInputState = reactive({
  isChanged: false,
  validate: false,
  validateSuccess: false,
  validateWrong: false,
  phone: '',
  fullPhone: '',
  mask: '',
  countryCode: 'US',
  options: {
    id: props.id,
    maxlength: undefined,
    placeholder: '050 123 4567',
    required: true,
    type: 'tel',
    styleClasses: 'vue-tel-input__input',
  },
  tz: dayjs.tz.guess(),
  isInitEnd: false,
  isSuccess: false,
})

const autoCountryCode = ref<string | null>(props.countryByTz[state.tz] || state.countryCode)

const replaceNumbersToMaska = (NationalNumb: string): string => {
  return NationalNumb.replace(/\d/g, () => '#')
}

const resetValidate = (): void => {
  state.validateSuccess = false
  state.validateWrong = false
}

const countryChanged = (event: CountryPhoneData): void => {
  const countryCode = event ? event.iso2 : state.countryCode
  const formatNationalNumb = getExampleNumber(countryCode, examples)!.formatNational()
  const maxLength = formatNationalNumb.length
  const formatNationalNumbMaska = replaceNumbersToMaska(formatNationalNumb)

  // clear autoCountryCode
  autoCountryCode.value = null
  // clear phone
  if (state.isInitEnd) {
    state.phone = ''
  } else {
    if (props.value !== '') {
      state.isInitEnd = true
      const phone = parsePhoneNumber(props.value)
      state.phone = hasProp(phone, 'nationalNumber')
        ? phone.formatNational()
        : ''
    } else {
      state.phone = ''
    }
  }
  // change other options
  state.countryCode = countryCode
  state.options.maxlength = maxLength
  state.options.placeholder = formatNationalNumb
  state.mask = formatNationalNumbMaska

  // reset validation
  resetValidate()
}

const setSuccess = (value: boolean): void => {
  state.isSuccess = value
}
const setValidateWrong = (value: boolean): void => {
  state.validateWrong = value
}

const onValidate = (event: ValidatePhoneData): void => {
  state.validate = !!event.valid

  if (event.number) {
    state.fullPhone = event.number
  }
  if (event.valid && event.number) {
    emit('change', {
      id: props.id,
      name: props.name,
      value: event.valid ? event.number : '',
      valid: state.validate ? state.validate : false,
    })
    setSuccess(event.number !== '' && event.valid)
  } else {
    if (state.isChanged) {
      emit('change', {
        id: props.id,
        name: props.name,
        value: '',
        valid: false,
      })
      setSuccess(false)
      onChange()
    }
    state.isChanged = true
  }
}

const onChange = (): void => {
  if (state.validate) {
    setSuccess(true)
  } else {
    setValidateWrong(true)
  }
}

const onInput = (): void => {
  resetValidate()
  emit('input')
}

const onBlur = (): void => {
  if (state.phone !== '') {
    state.isChanged = true
  }
}

</script>

<template>
  <div
    class="ui-def-phone"
    :class="{ 'is-invalid': (!state.validate && changeTrigger > 0) || (mode === 'error'), 'is-valid': state.validate && changeTrigger > 0 }"
  >
    <label
      v-if="label"
      :for="id"
      class="ui-def-phone__label"
    >
      {{ label }}
      <span
        v-if="required"
        class="required-indicator"
      >
        *
      </span>
    </label>
    <div class="ui-def-phone__box">
      <VueTelInput
        :id="id"
        ref="inputRef"
        v-model="state.phone"
        v-maska="state.mask"
        mode="national"
        :ignored-countries="ignoredCountries"
        :default-country="autoCountryCode ? autoCountryCode : state.countryCode"
        :input-options="state.options"
        :preferred-countries="['US', 'GB']"
        :valid-characters-only="true"
        :auto-format="true"
        :disabled="disabled"
        :data-qa="dataQa"
        :class="{
          'is-error': state.validateWrong || (!state.validate && changeTrigger > 0) || (mode === 'error'),
          'is-success': state.isSuccess
        }"
        @country-changed="countryChanged"
        @validate="onValidate"
        @change="onChange"
        @input="onInput"
        @blur="onBlur"
      >
        <template #arrow-icon>
          <span />
        </template>
      </VueTelInput>
    </div>
    <TextTertiary
      v-if="info"
    >
      {{ info }}
    </TextTertiary>
    <div
      v-if="(!state.validate && changeTrigger > 0) || (mode === 'error' && invalidMsg)"
      class="input-error"
    >
      {{ invalidMsg }}
    </div>
  </div>
</template>

<style scoped lang="scss" src="./PhoneInput.scss" />
